import "./App.scss";
import 'react-circular-progressbar/dist/styles.css';
import "react-toggle/style.css"

import React from "react";
import Toggle from 'react-toggle'
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import { IoPlaySharp, IoPauseSharp } from 'react-icons/io5'
import { AiOutlineLoading } from 'react-icons/ai'

import {
  Attentions, Gaze, KeyPlayer, Lv2Player,
  PlayerID, Player,
  VideoID, Video, IComment, PlayerFlag
} from '@types'
import { PLAYER_META } from '@const'

import { DEBUG } from 'common/@const'
import { globalFIdxToLocal } from "common/@utils";

import {
  Visualizer,
  Overlay,
  Court,
  CommentBox,
  Danmu,
  OnBoard,
  Contact
} from './components'
import { Timeline } from 'common/@components'

import { params } from "param";
import { IBallVideoEnv } from "VideoEnv";
import { getComment, postComment, subscribe } from "filebase";

export const videoEnv = new IBallVideoEnv()

// now this flag is a bit messy, some are applied to pickPlayer, some are applied to rendering
const enabledFeatures = {
  vis_off_ring: true,
  vis_def_sheild: true,
  vis_key_player: true,
  vis_empty_player: false,
  vis_top_view: false,
  gaze_focus: true,
  gaze_filter: false,
}
export type EnabledFeatures = typeof enabledFeatures



interface State {
  videos: Video[]
  pauseLoading: VideoID | undefined
  currentVideoIdx: number
  currentFrameIdx: number
  currentGaze: Gaze | undefined
  currentFrame?: HTMLVideoElement
  // player status
  gazePlayers?: Player[]
  attentions?: Attentions
  attentionsOverTime?: Attentions[]
  // pickedPlayers: Record<number, { lv1Players: Record<PlayerID, KeyPlayer>, lv2Players: Record<PlayerID, Lv2Player> }>
  pickedPlayers: {
    lv1: Partial<Record<PlayerID, KeyPlayer>>,
    lv2: Partial<Record<PlayerID, Lv2Player>>
  }
  comments: IComment[],

  // enable features:
  features: EnabledFeatures
  playerFlag: Partial<Record<PlayerID, PlayerFlag>>

  playing: boolean
}

export class App extends React.Component<{}, State> {

  state: State = {
    // playing
    pauseLoading: undefined,
    videos: [],
    currentVideoIdx: 0,
    currentFrameIdx: 0,
    currentFrame: undefined,
    currentGaze: undefined,
    gazePlayers: undefined,
    attentions: undefined,
    attentionsOverTime: undefined,
    pickedPlayers: { lv1: {}, lv2: {} },
    comments: [],
    //
    features: enabledFeatures,
    playerFlag: {},
    //
    playing: false,
  }

  #visualizer: React.RefObject<Visualizer> = React.createRef()
  #commentBox: React.RefObject<CommentBox> = React.createRef()
  #danmu: React.RefObject<Danmu> = React.createRef()

  get currentVideo() { return this.state.videos[this.state.currentVideoIdx] }

  async componentDidMount() {
    // should load parameters here

    // get the data from the server and construct a data tower for this video
    const videos = await Promise.all(params.VIDEO_IDS.map(async (videoId: string) => {

      try {
        const { max_frame: maxFrame, start_frame = 0, fps, h, w, version } = (await fetch(`${process.env.PUBLIC_URL}/assets/${params.GAME_ID}/${videoId}/${videoId}-meta.json`).then(r => r.json()))
        const isTransit = videoId.endsWith(',t')

        return {
          gameId: params.GAME_ID,
          id: videoId as VideoID,
          maxFrame,
          startFame: start_frame,
          isTransit,
          frameRate: fps, //params.GAME_ID === 'game1' ? 29.97 : 29.81,
          loadedData: 0,
          loadedVideo: 0,
          width: w,
          height: h,
          version,
        }
      } catch (e) {
        console.log('Something wrong with maxjson', videoId)
        throw e
      }
    }))
    this.setState({ videos })

    // set up videoEnv
    videoEnv.videos = videos

    videoEnv.onGaze = (currentGaze, gazePlayers) => this.setState({ currentGaze, gazePlayers })
    videoEnv.onUpdateLvInt = attentions => this.setState({ attentions })
    videoEnv.onLoad = (videoId: VideoID, type: 'Data' | 'Video', progress) => {
      const { videos } = this.state
      const videoIdx = videos.findIndex(v => v.id === videoId)
      if (videoIdx !== -1) {
        videos[videoIdx] = {
          ...videos[videoIdx],
          [`loaded${type}`]: progress === undefined ? 1 : progress
          // loaded: videoEnv.isLoaded(videos[videoIdx])
        }
        this.setState({ videos: [...videos] })
      }
    }
    videoEnv.onUnload = (videoId: VideoID, type: 'Data' | 'Video') => {
      const { videos } = this.state
      const videoIdx = videos.findIndex(v => v.id === videoId)
      if (videoIdx !== -1) {
        videos[videoIdx] = {
          ...videos[videoIdx],
          [`loaded${type}`]: 0
          // loaded: videoEnv.isLoaded(videos[videoIdx])
        }
        this.setState({ videos: [...videos] })
      }
    }

    videoEnv.onPause = (videoId) => this.setState({ playing: false })
    videoEnv.onPlay = (videoId) => this.setState({ playing: true, pauseLoading: undefined })

    // setup videoEngine
    // invoke the handler for each frame
    videoEnv.addOnFrameListener(this.pushFrameHistory)
    videoEnv.addOnFrameListener(async (videoId, frameIdx, ts, videoFrame) => {
      const { videos } = this.state
      const newCurVideoIdx = videos.findIndex(v => v.id === videoId)
      this.setState({
        currentFrameIdx: frameIdx,
        currentVideoIdx: newCurVideoIdx,
        currentFrame: videoFrame
      })
    })

    // load the frames
    const { currentVideoIdx } = this.state
    await videoEnv.fetchVideos()
    // preload videos
    await videoEnv.loadVideo(videos[currentVideoIdx])
    const inGamePlayerIds: PlayerID[] = Array.from(new Set(
      Object.values(videoEnv.frames ?? {}).flatMap(f => f.players?.map(p => p.id))
    )).filter(p => p !== undefined) as PlayerID[]

    await videoEnv.loadBins(inGamePlayerIds)
    // init
    const comments = await getComment()
    this.setState({ comments })
    // subscribe
    subscribe((newComments) => {
      const { comments } = this.state
      newComments = newComments.filter(c => comments.every(d => d.id !== c.id))

      this.setState({
        comments: [
          ...comments,
          ...newComments
        ]
      })
      newComments.forEach(c => this.#danmu.current?.push(c))
    })

    // check players
    this.setState({
      playerFlag: inGamePlayerIds
        .reduce((o, pId) => {
          if (pId) {
            o[pId] = 'Auto'
          }
          return o
        }, {} as Partial<Record<PlayerID, 'Auto'>>)
    })
  }

  componentWillUnmount() {
    // dataTower.onGaze = undefined
    // dataTower.onUpdateLvInt = undefined
    // dataTower.onUpdateLvSync = undefined
    // dataTower.onLoadToMem = undefined
    // dataTower.onUnloadToMem = undefined
    // videoEngine.clean()
    // videoEngine.onLoadToMem = undefined
    // videoEngine.onPause = undefined
    // videoEngine.onPlay = undefined
  }

  pushFrameHistory = (videoId: string, localFrameIdx: number, ts: number) => {
    const { videos } = this.state
    const videoIdx = videos.findIndex(v => v.id === videoId)

    if (videoIdx === -1) {
      videoEnv.pause()
      return
    }

    const video = videos[videoIdx]
    videoEnv.pushFrameHistory({ videoId: video.id, fIdx: localFrameIdx, ts })
  }

  onClickInterval = async (clipId: number, globalFrameIdx: number) => {
    // 直接给videoFrame设帧
    const { videos } = this.state
    const { videoIdx, frameIdx } = globalFIdxToLocal(globalFrameIdx, videos)
    videoEnv!.pause();
    await videoEnv!.seekTo(videos[videoIdx], frameIdx)
  }

  onPlayVideo = async () => {
    const { playing } = this.state
    if (playing) {
      videoEnv!.pause()
    } else {
      videoEnv!.play()
    }
  }

  loadedPrecentage() {
    const { videos } = this.state
    // assuming loading data takes 2x times than loading videos
    const value = videos.reduce((o, d) => o += d.loadedData * 2 + d.loadedVideo, 0) / videos.reduce((o, v) => o + (v.isTransit ? 1 : 3), 0)
    return value
  }

  renderProgressbar() {
    const value = this.loadedPrecentage() * 100
    return value === 100 ? null : <div style={{
      position: 'fixed',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)'
    }}>
      <CircularProgressbarWithChildren
        value={value}
        // text={'test'}
        styles={buildStyles({
          pathColor: `rgb(62, 152, 199)`,
          textColor: '#f88',
          trailColor: '#d6d6d6',
          backgroundColor: '#3e98c7',
        })}
      >
        <div style={{ color: '#f88', fontSize: '40px' }}>{`${value | 0}%`}</div>
        <div style={{ color: '#f88', fontSize: '24px' }} >~1min</div>
      </CircularProgressbarWithChildren>
    </div>
  }

  renderToggle() {
    const { features } = this.state
    return <div className="featurePanel">
      <h4 style={{ textAlign: 'center' }}>Features</h4>
      {Object.entries(features)
        .filter(([k, v]) => k !== 'onPlayers' && k !== 'offPlayers')
        .map(([k, v]) => {

          return <div className="featureItem" key={k}>
            <Toggle
              key={k}
              id='cheese-status'
              // defaultChecked={v as boolean}
              checked={v as boolean}
              onChange={(e) => {
                ; (features as any)[k] = !v
                this.setState({ features })
              }} />
            <span>{k.split('_').join(' ')}</span>
          </div>
        })}
    </div>
  }

  renderPlayerToggle() {
    const { playerFlag } = this.state
    return <div className="playerPanel">
      <h4 style={{ textAlign: 'center' }}>Players</h4>
      <div className="btn-group">
        {['Auto', 'On', 'Off'].map(v =>
          <button key={`btn_${v}`}
            onClick={() => {
              const { playerFlag } = this.state
              Object.keys(playerFlag)
                .forEach(pId => {
                  playerFlag[+pId as PlayerID] = v as PlayerFlag
                })
              this.setState({ playerFlag: { ...playerFlag } })
            }}>{v} All</button>)}
      </div>
      <div className="gridContainer">
        {Object.entries(playerFlag)
          .sort((a, b) => {
            const { team: teamA } = PLAYER_META[+a[0] as PlayerID]
            const { team: teamB } = PLAYER_META[+b[0] as PlayerID]

            return teamA.localeCompare(teamB)
          })
          .map(([pId, v]) => {
            const { team } = PLAYER_META[+pId as PlayerID]

            return <>
              <span className="gridItem"
                key={`${pId}_name`}
                style={{
                  color: ['GSW', 'LAC'].includes(team)
                    ? 'steelblue'
                    : '#ff6347',
                  fontWeight: 500
                }}
              >
                {PLAYER_META[+pId as PlayerID].ln}
              </span>
              <label className="gridItem" key={`${pId}_select`}>
                <select name="pflag" id="pflag" value={v}
                  onChange={e => {
                    playerFlag[+pId as PlayerID] = e.target.value as PlayerFlag
                    this.setState({ playerFlag: { ...playerFlag } })
                  }}
                >
                  {['Auto', 'On', 'Off', 'TXT'].map(value =>
                    <option key={value} value={value} disabled={value === 'TXT'}>{value}</option>
                  )}
                </select>
              </label>
            </>
          })}
      </div>
    </div>
  }

  render() {
    const {
      currentVideoIdx,
      currentFrameIdx,

      features,
      playerFlag,

      currentGaze,
      currentFrame,
      gazePlayers,

      pauseLoading,
      attentions,
      attentionsOverTime,
      pickedPlayers,
      comments,
      // sync,
      playing,
      videos,
    } = this.state;
    const currentFrameData = videoEnv.frames?.[currentFrameIdx]

    return (
      <>
        <OnBoard></OnBoard>
        {this.renderProgressbar()}
        {features.vis_top_view && <Court
          currentFrameData={currentFrameData}
        />}
        <div className="up">
          <div className="left">
            <Visualizer ref={this.#visualizer}
              // video
              currentVideo={this.currentVideo}
              currentFrameIdx={currentFrameIdx}
              currentFrame={currentFrame}
              // feature
              features={features}
              playerFlags={playerFlag}
              // gaze
              gaze={currentGaze}
              gazePlayers={gazePlayers}
              onPickPlayers={(fIdx, lv1Players, lv2Players) => {
                const vIdx = videos.findIndex(v => v.id === this.currentVideo.id)
                if (vIdx === -1) return

                this.setState({ pickedPlayers: { lv1: lv1Players, lv2: lv2Players } })
                // const globalFIdx = localFIdxToGlobalFIdx(vIdx, fIdx, videos)
                // this.setState({
                //   pickedPlayers: { ...pickedPlayers, [globalFIdx]: { lv1Players, lv2Players } }
                // })
              }}
            >

              <Overlay
                currentVideo={this.currentVideo}
                currentFrameData={currentFrameData}
                gaze={DEBUG.GAZE ? currentGaze : undefined}
                gazePlayers={gazePlayers}
                bbox={false}
              />
              <Danmu
                ref={this.#danmu}
                videoId={this.currentVideo?.id}
                frameIdx={currentFrameIdx}
                comments={comments}
              />
            </Visualizer>

            <div className="controls">
              <button
                id="playpause"
                className="play button is-outlined"
                type="button"
                disabled={pauseLoading !== undefined || this.loadedPrecentage() !== 1}
                onClick={() => this.onPlayVideo()}
              >
                {playing
                  ? <IoPauseSharp style={{ transform: 'scale(1.5)' }} />
                  : pauseLoading !== undefined
                    ? <AiOutlineLoading style={{ transform: 'scale(1.5)' }} />
                    : <IoPlaySharp style={{ transform: 'scale(1.5)' }} />}
              </button>
              <CommentBox
                ref={this.#commentBox}
                existPlayers={Object.keys(playerFlag).map(p => +p) as PlayerID[]}
                onUpdatePlayerEntity={pIds => {
                  const { playerFlag } = this.state

                  Object.keys(playerFlag)
                    .forEach(pId => {
                      if (pIds.includes(+pId as PlayerID)) {
                        playerFlag[+pId as PlayerID] = 'TXT'
                      } else if (playerFlag[+pId as PlayerID] === 'TXT') {
                        // switch back to Auto
                        playerFlag[+pId as PlayerID] = 'Off'
                      }

                    })
                  this.setState({ playerFlag: { ...playerFlag } })
                }}
                onSend={async (text) => {
                  const gameId = this.currentVideo.gameId
                  const videoId = this.currentVideo.id
                  const frameIdx = currentFrameIdx

                  const docId = await postComment({
                    content: text.trim(),
                    gameId,
                    videoId,
                    frameIdx,
                    features,
                    playerFlag,
                    pickedPlayers,
                  })
                  // will be updated in the subscribe callback
                  if (docId) {
                    // const newComment = { id: docId, content: text, gameId, videoId, frameIdx }
                    // this.setState({
                    //   comments: [
                    //     ...comments,
                    //     newComment
                    //   ]
                    // })
                    // this.#danmu.current?.push(newComment)
                    this.#commentBox.current?.clean()
                  }
                }}
              ></CommentBox>
            </div>
          </div>
          {/* <div className="divide"> </div> */}

          <div className="right">
            {this.renderToggle()}
            {this.renderPlayerToggle()}

            <Contact></Contact>
          </div>
        </div>

        {DEBUG.TIMELINE &&
          <Timeline
            mode="S"
            videos={videos}
            currentVideoIdx={currentVideoIdx}
            currentFrameIdx={currentFrameIdx}
            enableClips={false}
            // pickedPlayers={pickedPlayers}
            onClickInterval={this.onClickInterval}
          />
        }
      </>)
  }
}